import { FC, useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, Button, Col, Modal, ModalBody, Row } from "reactstrap";
import { Icon, TooltipComponent } from "../../../../components/Component";
import FormSelect from "../../../form-components/SelectComponent";
import supplierApis from "../../../../api/master/supplier";
import FormInput from "../../../form-components/InputComponent";
import FormDatePicker from "../../../form-components/DatePicker";
import FormTextArea from "../../../form-components/TextAreaComponent";
import FormInputSwitch from "../../../form-components/FormInputSwitch";
import { Popover } from "antd";
import deliveryAddressApis from "../../../../api/master/deliveryAddress";
import sitebreakdownMasterApis from "../../../../api/master/sitebreakdownMaster";
import sitesApis from "../../../../api/master/sites";
import { nanoid } from "@reduxjs/toolkit";
interface SelectOptions {
  label: string;
  value: any;
}

const WORK_LEVEL_TO_SEGMENT: Record<string, string> = {
  TOWER_BLOCK: "Tower",
  VILLA: "Villa",
  FLOOR: "Floor",
  FLAT: "Flat",
};

const MaterialModal: FC<any> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  materialsList,
  siteId,
  deliveryAddressType,
  materialCategoryOptions,
}) => {
  const [sm, setSm] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);

  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialData, setMaterialData] = useState<any>();
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);

  const [deliveryAddressList, setDeliveryAddressList] = useState<any>([]);
  const [deliveryAddressOptions, setDeliveryAddressOptions] = useState<
    SelectOptions[]
  >([]);

  const [towerOptions, setTowerOptions] = useState<SelectOptions[]>([]);
  const [floorOptions, setFloorOptions] = useState<SelectOptions[]>([]);
  const [flatOptions, setFlatOptions] = useState<SelectOptions[]>([]);
  const [locationOptions, setLocationOptions] = useState<SelectOptions[]>([]);
  const [isTowerLoading, setIsTowerLoading] = useState(false);
  const [isFloorLoading, setIsFloorLoading] = useState(false);
  const [isFlatLoading, setIsFlatLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [siteSegmentLevel, setSiteSegmentLevel] = useState<string | null>(null);
  const isVillaFlow = siteSegmentLevel === "Villa";
  const unitFieldLabel = isVillaFlow ? "Villa" : "Tower / Block";
  const unitFieldPlaceholder = isVillaFlow
    ? "Select Villa"
    : "Select Tower / Block";

  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;
  const deliveryAddressId = watch("deliveryAddressId");
  const [alertInfo, setAlertInfo] = useState({
    type: "", // 'success' or 'error'
    message: "",
    errors: [],
  });
  const [dmsFile, setDmsFile] = useState<any>(null);
  const files = watch("attachment");
  const towerBlockVillaIdWatch = watch("towerBlockVillaId");
  const floorNoWatch = watch("floorNo");

  useEffect(() => {
    if (files && files[0]?.size && files[0].size > 2097152) {
      setValue("attachment", null);
      setAlertInfo({
        type: "error",
        message: "File size should be less than 2 MB",
        errors: [],
      });
    }
  }, [files]);

  const fetchUom = async (materialId: any, materialData: any, uomId: any) => {
    try {
      const materialUom = materialData?.find(
        (item: any) => item.materialId == materialId
      );
      if (!materialUom) return;
      const _uomOptions = [
        {
          value: materialUom?.primaryUomId,
          label: materialUom?.PrimaryUomName,
        },
      ];
      if (
        materialUom?.secondaryUomId &&
        materialUom?.primaryUomId != materialUom?.secondaryUomId &&
        materialUom?.PrimaryUomName != materialUom?.secondaryUomName
      ) {
        _uomOptions.push({
          value: materialUom?.secondaryUomId,
          label: materialUom?.secondaryUomName,
        });
      }
      setValue("uomId", uomId ? uomId : materialUom?.primaryUomId);
      setUomOptions(_uomOptions);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchMaterials = async (categoryId: any, materialId: any) => {
    try {
      const { data, status } = await supplierApis.fetchCategoryProducts(
        categoryId
      );
      if (status) {
        const materialOption = data.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
        }));
        if (
          (formData.action === "add" || formData.index === null) &&
          deliveryAddressType == "Order Level"
        ) {
          if (Array.isArray(materialOption)) {
            const filteredOptions = materialOption.filter(
              (option) =>
                !materialsList || !materialsList.includes(option.value)
            );
            setMaterialOptions(filteredOptions);
          }
        } else {
          setMaterialOptions(materialOption);
        }
        setMaterialData(data);
        setValue("materialName", materialId);
        return data;
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAlertInfo({
        type: "",
        message: "",
        errors: [],
      });
    }, 2500);
    return () => clearTimeout(timeoutId);
  }, [alertInfo]);

  const fetchDeliveryAddress = async () => {
    try {
      if (siteId) {
        const { data, status, message } =
          await deliveryAddressApis.fetchAddressBySite(siteId);
        if (status) {
          let deliveryAddressOptions = data.map((item: any) => ({
            label: item.deliveryLocationName,
            value: item.id.toString(),
          }));
          setDeliveryAddressOptions(deliveryAddressOptions);
          setDeliveryAddressList(data);
        } else {
          console.log(message);
        }
      }
    } catch (error) {
      console.log("Error fetching delivery addresses:", error);
    }
  };

  useEffect(() => {
    fetchDeliveryAddress();
  }, [siteId]);

  // --- Site Hierarchy Logic ---
  const loadTowerOptions = useCallback(async () => {
    if (!siteId) {
      setTowerOptions([]);
      return;
    }
    setIsTowerLoading(true);
    try {
      const response = await sitebreakdownMasterApis.fetchUnitsBySiteId({
        siteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        const isVilla = siteSegmentLevel === "Villa";
        // API returns the correct set of units based on the site's segment
        // configuration (villas for villa sites, towers for tower sites).
        // Only filter by `unitType` when the field is actually present on the
        // payload — otherwise we'd wipe out valid options (villa rows don't
        // carry a unitType field).
        const hasUnitType = response.data.some(
          (unit: any) => unit?.unitType != null && unit.unitType !== ""
        );
        const filtered = hasUnitType
          ? response.data.filter((unit: any) => {
              const type = (unit.unitType || "").toLowerCase();
              return isVilla ? type === "villa" : type !== "villa";
            })
          : response.data;
        const options = filtered.map((unit: any) => ({
          value: unit.id,
          label: isVilla
            ? unit.unitName || unit.locationName || unit.subUnitName || ""
            : unit.unitName || "",
        }));
        setTowerOptions(options);
      } else {
        setTowerOptions([]);
      }
    } catch (error) {
      console.log("Failed to load tower options", error);
      setTowerOptions([]);
    } finally {
      setIsTowerLoading(false);
    }
  }, [siteId, siteSegmentLevel]);

  const loadFloorOptions = useCallback(
    async (unitId?: number | string) => {
      if (!siteId || !unitId) {
        setFloorOptions([]);
        return;
      }
      setIsFloorLoading(true);
      try {
        const response =
          await sitebreakdownMasterApis.fetchFloorNumbersByUnitId({
            siteId,
            unitId,
          });
        if (response?.status && Array.isArray(response.data)) {
          setFloorOptions(
            response.data.map((floor: any) => ({
              value: floor.floorNo,
              label: floor.floorNo,
            }))
          );
        } else {
          setFloorOptions([]);
        }
      } catch (error) {
        console.log("Failed to load floor options", error);
        setFloorOptions([]);
      } finally {
        setIsFloorLoading(false);
      }
    },
    [siteId]
  );

  const loadFlatOptions = useCallback(
    async (unitId?: number | string, floorNo?: string | null) => {
      if (!siteId || !unitId) {
        setFlatOptions([]);
        return;
      }
      setIsFlatLoading(true);
      try {
        const response = await sitebreakdownMasterApis.fetchSubUnitsByUnitId({
          siteId,
          unitId,
          floorNo: floorNo ?? undefined,
        });
        if (response?.status && Array.isArray(response.data)) {
          setFlatOptions(
            response.data.map((flat: any) => ({
              value: flat.id,
              label: flat.subUnitName || flat.unitName,
            }))
          );
        } else {
          setFlatOptions([]);
        }
      } catch (error) {
        console.log("Failed to load flat options", error);
        setFlatOptions([]);
      } finally {
        setIsFlatLoading(false);
      }
    },
    [siteId]
  );

  const loadLocationOptions = useCallback(async () => {
    if (!siteId) {
      setLocationOptions([]);
      return;
    }
    setIsLocationLoading(true);
    try {
      const response = await sitebreakdownMasterApis.fetchLocationsBySiteId({
        siteId,
      });
      if (response?.status && Array.isArray(response.data)) {
        setLocationOptions(
          response.data.map((location: any) => ({
            value: location.id,
            label: location.locationName,
          }))
        );
      } else {
        setLocationOptions([]);
      }
    } catch (error) {
      console.log("Failed to load location options", error);
      setLocationOptions([]);
    } finally {
      setIsLocationLoading(false);
    }
  }, [siteId]);

  // Load tower and location options when modal opens
  useEffect(() => {
    if (!modal) return;
    if (siteId) {
      loadTowerOptions();
      if (!isVillaFlow) {
        loadLocationOptions();
      }
    } else {
      setTowerOptions([]);
      setFloorOptions([]);
      setFlatOptions([]);
      setLocationOptions([]);
    }
  }, [modal, siteId, isVillaFlow, loadTowerOptions, loadLocationOptions]);

  // Fetch site segment level so we know whether this is a Tower/Block or Villa flow
  useEffect(() => {
    if (!modal || !siteId) {
      setSiteSegmentLevel(null);
      return;
    }
    (async () => {
      try {
        const { data, status } = await sitesApis.fetchSegmentLevelBySiteId(
          siteId
        );
        if (status && data) {
          const level =
            typeof data === "string"
              ? data
              : data.segmentLevel || data.segment || null;
          setSiteSegmentLevel(level);
        } else {
          setSiteSegmentLevel(null);
        }
      } catch (error) {
        console.log("Failed to fetch site segment level", error);
        setSiteSegmentLevel(null);
      }
    })();
  }, [modal, siteId]);

  // Load floor options when tower changes (skipped in villa flow)
  useEffect(() => {
    if (!modal || !siteId) return;
    if (isVillaFlow) {
      setFloorOptions([]);
      setValue("floorNo", null);
      return;
    }
    if (towerBlockVillaIdWatch) {
      loadFloorOptions(towerBlockVillaIdWatch);
    } else {
      setFloorOptions([]);
      setValue("floorNo", null);
    }
  }, [
    isVillaFlow,
    loadFloorOptions,
    modal,
    setValue,
    siteId,
    towerBlockVillaIdWatch,
  ]);

  // Load flat options when tower/floor changes (skipped in villa flow)
  useEffect(() => {
    if (!modal || !siteId) return;
    if (isVillaFlow) {
      setFlatOptions([]);
      setValue("flatId", null);
      return;
    }
    if (towerBlockVillaIdWatch) {
      loadFlatOptions(towerBlockVillaIdWatch, floorNoWatch ?? null);
    } else {
      setFlatOptions([]);
      setValue("flatId", null);
    }
  }, [
    floorNoWatch,
    isVillaFlow,
    loadFlatOptions,
    modal,
    setValue,
    siteId,
    towerBlockVillaIdWatch,
  ]);

  // Clear location in villa flow
  useEffect(() => {
    if (!modal || !siteId) return;
    if (isVillaFlow) {
      setLocationOptions([]);
      setValue("locationId", null);
    }
  }, [isVillaFlow, modal, setValue, siteId]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    console.log("formData", formData);
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    if (modal && formData.item) {
      console.log("formData.item", formData.item);
      const {
        materialCategory,
        materialName,
        quantity,
        uomId,
        deliveryAddressId,
        description,
        needByDate,
        file,
        dms,
        workLevelSetting: itemWorkLevelSetting,
        towerBlockVillaId,
        floorNo,
        flatId,
        locationId,
      } = formData.item;
      setValue("materialCategory", materialCategory);
      (async () => {
        const _materialData = await fetchMaterials(
          materialCategory,
          materialName
        );
        fetchUom(materialName, _materialData, uomId);
      })();
      setValue("quantity", quantity);
      setValue("materialUsage", formData.item.materialUsage || "");
      setValue("installationRequired", formData.item.installationRequired || false);
      setValue("description", description);
      setValue("attachment", file);
      if (formData.index != null) {
        setValue("deliveryAddressId", deliveryAddressId);
        setValue("needByDate", needByDate);
      }
      if (dms?.length > 0) {
        setDmsFile(dms[0]);
      }
      // Restore hierarchy fields
      if (towerBlockVillaId) {
        setValue("towerBlockVillaId", towerBlockVillaId);
        loadFloorOptions(towerBlockVillaId);
        loadFlatOptions(towerBlockVillaId, floorNo);
      }
      if (floorNo) setValue("floorNo", floorNo);
      if (flatId) setValue("flatId", flatId);
      if (locationId) setValue("locationId", locationId);
      if (itemWorkLevelSetting) {
        setValue("workLevelSetting", itemWorkLevelSetting);
      }
    }
  }, [modal]);

  const ShowAddressPopOver = ({ id, text, options, matchKey }: any) => {
    if (!id) {
      return <></>;
    }
    let address = options?.find((item: any) => item[matchKey] == id);
    if (!address) {
      return <></>;
    }
    return (
      <Popover
        content={
          <ul>
            <li>
              {address?.addressLine1} {address?.addressLine2}
            </li>
            <li>
              {address?.city}, {address?.stateName}, {address?.countryName} -{" "}
              {address?.pincode}
            </li>
          </ul>
        }
        title={address?.deliveryLocationName}
      >
        <a>{text}</a>
      </Popover>
    );
  };
  const submitForm = (data: any) => {
    const {
      materialCategory,
      materialName,
      quantity,
      description,
      uomId,
      needByDate,
      attachment,
      deliveryAddressId,
      materialUsage,
      installationRequired,
      workLevelSetting: workLevelValue,
      towerBlockVillaId: towerIdValue,
      floorNo: floorNoValue,
      flatId: flatIdValue,
      locationId: locationIdValue,
    } = data;

    const materialCategoryName = materialCategoryOptions.find(
      (item: any) => item.value == materialCategory
    )?.label;
    const materialLabel = materialOptions.find(
      (item) => item.value == materialName
    )?.label;
    const uomLabel = uomOptions.find((item) => item.value == uomId)?.label;

    const towerBlockVillaLabel =
      towerOptions.find((opt) => opt.value == towerIdValue)?.label ?? "";
    const floorLabel = floorNoValue ?? "";
    const flatLabel =
      flatOptions.find((opt) => opt.value == flatIdValue)?.label ?? "";
    const locationLabel =
      locationOptions.find((opt) => opt.value == locationIdValue)?.label ?? "";

    const hierarchyData = siteId
      ? {
          workLevelSetting: workLevelValue ?? null,
          segmentLevel: workLevelValue
            ? WORK_LEVEL_TO_SEGMENT[workLevelValue] ?? siteSegmentLevel ?? null
            : siteSegmentLevel ?? null,
          towerBlockVillaId: towerIdValue ?? null,
          towerBlockVillaLabel,
          floorNo: floorNoValue ?? null,
          floorLabel,
          flatId: flatIdValue ?? null,
          flatLabel,
          locationId: locationIdValue ?? null,
          locationLabel,
        }
      : {};

    if (formData.action == "add" || formData.index == null) {
      append({
        key: nanoid(),
        materialCategory,
        materialCategoryName,
        materialName,
        materialLabel,
        quantity,
        uomId,
        uomLabel,
        description,
        needByDate,
        file: attachment,
        deliveryAddressId,
        materialUsage,
        installationRequired,
        ...hierarchyData,
      });
    } else {
      update(formData.index, {
        ...formData.item,
        key: nanoid(),
        materialCategory,
        materialCategoryName,
        materialName,
        materialLabel,
        quantity,
        uomId,
        uomLabel,
        description,
        needByDate,
        file: attachment,
        deliveryAddressId,
        materialUsage,
        installationRequired,
        dms: dmsFile ? formData?.item?.dms : [],
        ...hierarchyData,
      });
    }
    closeModal();
  };

  return (
    <Modal isOpen={modal} toggle={closeModal} size="xl" backdrop="static">
      <ModalBody>
        <h5 className="title">
          {formData.action == "add" || formData.index == null
            ? "Add New Line"
            : "Update Line"}
        </h5>
        <div style={{ marginTop: "15px" }}></div>
        {alertInfo.message && (
          <div className="mb-3 mt-1">
            <Alert
              color={alertInfo.type === "error" ? "danger" : "success"}
              className="alert-icon"
            >
              <strong>{alertInfo.message}</strong>
              <Icon
                name={
                  alertInfo.type === "error" ? "alert-circle" : "check-circle"
                }
              />
              {alertInfo.errors.length > 0 ? (
                <ul>
                  {alertInfo.errors.map((err: any, index: any) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                ""
              )}
            </Alert>
          </div>
        )}
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)}>
            <Row>
              <div className="col-md-3">
                <FormSelect
                  name="materialCategory"
                  label="Material Category"
                  required={true}
                  options={materialCategoryOptions}
                  placeholder="Select Material Category"
                  onChange={(e: any) => {
                    fetchMaterials(e, null);
                  }}
                />
              </div>
              <div className="col-md-3">
                <FormSelect
                  name="materialName"
                  label="Material Name"
                  required={true}
                  options={materialOptions.filter(
                    (option) => !selectedMaterials.includes(option.value)
                  )}
                  placeholder="Select Material Name"
                  onChange={(e: any) => {
                    fetchUom(e, materialData, null);
                  }}
                />
              </div>
              <div className="col-md-3">
                <FormInput
                  name="materialUsage"
                  label="Material Usage"
                  required={true}
                  type="text"
                  placeholder="Enter Material Usage"
                  tooltipText="Enter the usage of the material."
                />
              </div>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-3">
                <FormInput
                  type="number"
                  min={0.1}
                  required={true}
                  name="quantity"
                  label="Quantity"
                  placeholder="Enter Quantity"
                />
              </div>
              <div className="col-md-3">
                <FormSelect
                  name="uomId"
                  label="UOM"
                  required={true}
                  options={uomOptions}
                  placeholder="Units"
                />
              </div>
              <div className="col-md-3">
                <FormDatePicker
                  name="needByDate"
                  label="Need By Date"
                  required={true}
                  minDate={new Date()}
                />
              </div>
              <div className="col-md-3">
                <FormInputSwitch
                  name="installationRequired"
                  label="Installation Required"
                  optionLabel={watch("installationRequired") ? "Yes" : "No"}
                />
              </div>
            </Row>
            {siteId && (
              <>
                <div style={{ marginTop: "15px" }}></div>
                <Row>
                  <div className="col-md-3">
                    <FormSelect
                      name="towerBlockVillaId"
                      label={unitFieldLabel}
                      required={true}
                      options={towerOptions}
                      placeholder={unitFieldPlaceholder}
                      isLoading={isTowerLoading}
                    />
                  </div>
                  {!isVillaFlow && (
                    <>
                      <div className="col-md-3">
                        <FormSelect
                          name="floorNo"
                          label="Floor"
                          required={false}
                          options={floorOptions}
                          placeholder="Select Floor"
                          isLoading={isFloorLoading}
                        />
                      </div>
                      <div className="col-md-3">
                        <FormSelect
                          name="flatId"
                          label="Flat"
                          options={flatOptions}
                          placeholder="Select Flat"
                          isLoading={isFlatLoading}
                        />
                      </div>
                      <div className="col-md-3">
                        <FormSelect
                          name="locationId"
                          label="Location"
                          options={locationOptions}
                          placeholder="Select Location"
                          isLoading={isLocationLoading}
                        />
                      </div>
                    </>
                  )}
                </Row>
              </>
            )}
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              {deliveryAddressType === "Line Level" && (
                <div className="col-md-6">
                  <FormSelect
                    name="deliveryAddressId"
                    label="Delivery Address"
                    required={true}
                    options={deliveryAddressOptions}
                    popOverOptions={
                      <ShowAddressPopOver
                        id={deliveryAddressId}
                        matchKey="id"
                        options={deliveryAddressList}
                        text="Address"
                      />
                    }
                    placeholder="Select Delivery Address"
                    tooltipText="Select the delivery address for the purchase order."
                  />
                </div>
              )}
              <Col md={6}>
                {((files && files[0]?.name) ||
                  dmsFile ||
                  dmsFile?.fileName) && (
                  <div className="form-group m-0">
                    <div className="form-label-wrapper">
                      <label className="form-label" htmlFor={"attachment"}>
                        Attachment (If any)
                      </label>
                      <TooltipComponent
                        iconClass="card-hint"
                        icon="help-fill"
                        direction="left"
                        id={`file-tooltip`}
                        text="e.g: Design specificaitons"
                        containerClassName={""}
                      />
                    </div>
                    <div
                      className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                      style={{ borderRadius: "3px" }}
                    >
                      <span>
                        {(files && files[0]?.name) || dmsFile?.fileName}
                      </span>
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                          setDmsFile(null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: `${
                      (files && files[0]?.name) || dmsFile?.fileName
                        ? "none"
                        : "block"
                    }`,
                  }}
                >
                  <FormInput
                    type="file"
                    name="attachment"
                    label="Attachment (If any) "
                    placeholder="Enter Attachment"
                    tooltipText="e.g: Design specificaitons"
                  />
                </div>
              </Col>
            </Row>
            <div style={{ marginTop: "15px" }}></div>
            <Row>
              <div className="col-md-12">
                <FormTextArea
                  name="description"
                  label="Description (if any)"
                  placeholder="Enter Description"
                  minLength={5}
                  maxLength={200}
                />
              </div>
            </Row>
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  {formData.action === "edit" && formData.index != null && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Update
                      </Button>
                    </li>
                  )}
                  {(formData.action == "add" || formData.index == null) && (
                    <li>
                      <Button
                        className="btn btn-primary btn-md"
                        type="submit"
                        color="primary"
                      >
                        Save
                      </Button>
                    </li>
                  )}
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        closeModal();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      </ModalBody>
    </Modal>
  );
};

export default MaterialModal;
